import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Col, Container, Row} from "react-bootstrap";
import {AgGridReact} from "ag-grid-react";
import AgGridToolbar from "components/AgGridToolbar/AgGridToolbar";
import ActionsRenderer from "components/ActionsRenderer";
import PageHeader from "components/PageHeader";
import useWindowDimensions from "hooks/Dimensions";
import {SwiftMessageContext} from "./context";
import {ActionPanelContext} from "context/action";
import {SecurityContext} from "context/security";
import {dateToString, formatDateString, stringToDate} from "utils/FormatUtils";
import * as URL from "service/UrlConstant";
import {
    ENABLE_SCROLL_BUTTONS_PAGINATION_SIZE,
    GRID_COLUMN_PROPERTY
} from "utils/Constants";
import {numberFormatter} from "utils/NumberUtils";
import {addPropertiesToGridColumns, getDisplayedNumberOfGridRows} from "../../../utils/gridColumnUtils";
import {UtilsContext} from "../../../context/utils";
import {createExcelExportParams, getFilteredColumns} from "../../../utils/ExcelExportUtils";
import ActionPanelLayout from "../../../layouts/ActionPanelLayout";
import ScrollButton from "../../../components/AgGridToolbar/ToolBarButtons/ScrollButton";
import SwiftParamsBadge from "./SwiftParamsBadge";


export default function SwiftMessageSearchPanel() {

    const {swiftMessageSearchDisplayState, setSwiftMessageSearchDisplayState,handlePageRequest, showBadge} = useContext(SwiftMessageContext);
    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [showExport, setShowExport] = useState(true);
    const [show, setShow] = useState(false)
    const [actionForAccount, setActionForAccount] = useState('');
    const {setHandleRefreshFunction, setHandleExportFunction} = useContext(ActionPanelContext);
    const {makePostRequest} = useContext(SecurityContext);
    const {isMobile} = useWindowDimensions();
    const {hiddenExportGridApi,paginationPageSize, setPaginationPageSize, showButton, setShowButton} = useContext(UtilsContext)
    const ssaResultsGrid = useRef(null);
    const myGrid = useRef(null);

    const defaultColDef = {
        sortable:true,
        resizable:true,
        floatingFilter:true,
        filter:true,
        enableCellChangeFlash:true,
        lockPinned:!!isMobile,
        minWidth: 138
    };

    const columnTypes ={
        flexColType: {
            sortable: false,
            floatingFilter: false,
            filter: false,
            enableCellChangeFlash: false,
            suppressMenu:true,
            suppressMovable:true,
        }
    }

    const exportSSAApproverList = (type, rowData, gridApi, onSuccess, onError) => {
        //Set Column definition
        hiddenExportGridApi.current.api.setColumnDefs(columnDefs);
        //Set Data
        hiddenExportGridApi.current.api.setRowData(rowData || swiftMessageSearchDisplayState.resData);
        let filteredColumnNames = getFilteredColumns(ssaResultsGrid);
        //Export as csv/excel
        type==='EXCEL' ?
            hiddenExportGridApi.current.api.exportDataAsExcel(createExcelExportParams('SSA', filteredColumnNames)) :
            hiddenExportGridApi.current.api.exportDataAsCsv({ columnKeys: filteredColumnNames})
    }

    const statusRenderer = function (props){
        if(props.value === 'Rejected')
            return <span className="red-cell">{lookupValue(statusMappings, props.value)}</span>;
        else if(props.value === 'Pending' || props.value === 'Partial')
            return <span className="orange-cell">{lookupValue(statusMappings, props.value)}</span>;
        else if(props.value === 'All' || props.value === 'Processed')
            return <span className="green-cell">{lookupValue(statusMappings, props.value)}</span>;
        else
            return <span>{lookupValue(statusMappings, props.value)}</span>;
    }

    const statusMappings = {
        'All': 'All',
        'Pending': 'Pending',
        'Processed' : 'Complete',
        'Rejected': 'No Good',
        'Partial': 'Partial',
    };

    useEffect(() => {
        setRowData(swiftMessageSearchDisplayState?.resData);
        setHandleExportFunction({handleExport:exportSSAApproverList, gridApi: gridApi});
    }, [swiftMessageSearchDisplayState])

    const lookupValue = (mappings, key) => {
        return mappings[key];
    };

    const handleExportActionClick = (field) => {
        setActionForAccount(field.savedName);
        setShow(true);
        setShowExport(true);
    }

    const statusFilterValueGetter = (row) => {
        return statusMappings[row.getValue("status")];
    }

    const columnDefs = [
        {headerName: 'Effective', field: 'effectiveDate', filter: 'agTextColumnFilter', valueGetter: (params)=>stringToDate(dateToString(params.data.effectiveDate))},
        {headerName: 'PmgReferenceId', field: 'id', filter:  'agTextColumnFilter'},
        {headerName: 'ClientTradeProcessIndicator', field: 'indProcessCltTrade', filter:  'agTextColumnFilter'},
        {headerName: 'MTMsgType', field: 'cdMtMsgType', filter:  'agTextColumnFilter'},
        {headerName: 'Date', field: 'messageDate', filter:  'agTextColumnFilter'},
        {headerName: 'IndDirection', field: 'indDirection', filter:  'agTextColumnFilter'},
        {headerName: 'RawMessage', field: 'message', filter: 'agTextColumnFilter'},
        {headerName: 'comments', field: 'comments', filter: 'agTextColumnFilter'},
        // {headerName: '', field: '', type: 'flexColType', suppressColumnsToolPanel: true},
    ];

    const updatedColumnDefs = useMemo(() => addPropertiesToGridColumns(columnDefs, GRID_COLUMN_PROPERTY), []);

    const onGridReady = (params) => {
        !isMobile && params.api.sizeColumnsToFit()
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    }
    const onDataRendered = () => {
        setShowButton(getDisplayedNumberOfGridRows(gridApi) >= ENABLE_SCROLL_BUTTONS_PAGINATION_SIZE);
    }

    const onPaginationChange = (params) => {
        setShowButton(getDisplayedNumberOfGridRows(gridApi) >= ENABLE_SCROLL_BUTTONS_PAGINATION_SIZE);
    }

    return(<>
        <Container fluid>
            <PageHeader text="Swift Activities">
                {(showBadge) && <SwiftParamsBadge onPageRequest={handlePageRequest} />}
                <h3 className="mt-4">Swift Messages</h3>
            </PageHeader>
            <AgGridToolbar swiftMessageScreen={true} gridApi={gridApi} gridColumnApi={gridColumnApi} onPageRequest={handlePageRequest} refreshCriteria={swiftMessageSearchDisplayState?.searchCriteria} rowData={rowData}
                           paginationPageSize={paginationPageSize}  setPaginationPageSize={setPaginationPageSize}
                           showButton={showButton} setShowButton={setShowButton}
            ></AgGridToolbar>
            <div
                ref={myGrid}
                id="myGrid"
                className="ag-theme-alpine ag-grid-block">
                <AgGridReact
                    frameworkComponents={{
                        statusRenderer: statusRenderer,
                        actionsRenderer: ActionsRenderer,
                    }}
                    ref={ssaResultsGrid}
                    paginationPageSize={paginationPageSize}
                    groupDefaultExpanded={'1'}
                    enableRowGroup={false}
                    suppressDragLeaveHidesColumns={true}
                    suppressMakeColumnVisibleAfterUnGroup={true}
                    suppressModelUpdateAfterUpdateTransaction={true}
                    suppressScrollOnNewData={true}
                    suppressAggFuncInHeader={true}
                    pagination={true}
                    allowShowChangeAfterFilter={false}
                    rowGroupPanelShow={'never'}
                    enableSorting={true}
                    enableFilter={false}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onDataRendered}
                    defaultColDef={defaultColDef}
                    columnDefs={updatedColumnDefs}
                    columnTypes={columnTypes}
                    rowData={rowData}
                    animateRows={true}
                    onPaginationChanged={onPaginationChange}
                >
                </AgGridReact>
            </div>
        </Container>
        {showButton && <ScrollButton showButton={showButton}/>}
        <ActionPanelLayout actionForAccount={actionForAccount} show={show} setShow={setShow}
                           showExport={showExport} setShowExport={setShowExport} gridApi={gridApi}
        />
    </>);

}
